import { z, EnumLike } from 'zod'
import { formatDateForApi, fromString } from '~/shared/lib/date'
import { sanitizePhoneNumber, sanitizePriceNumber } from '~/shared/lib/utils'

export const dateSchema = z
  .preprocess((arg, ctx) => {
    if (!arg) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Обязательное поле',
      })
    }

    if (typeof arg == 'string') {
      return fromString(arg)
    }
    return arg
  }, z.date())
  .transform(formatDateForApi)

export const phoneScheme = z
  .string()
  .superRefine((value, ctx) => {
    const sanitized = sanitizePhoneNumber(value)

    if (!sanitized.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Обязательное поле',
      })
    }

    if (sanitized.length > 0 && sanitized.length < 11) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Неверный номер телефона',
      })
    }
  })
  .transform(sanitizePhoneNumber)

export const priceRequiredScheme = z
  .string()
  .or(z.number())
  .superRefine((value, ctx) => {
    const sanitized = sanitizePriceNumber(value)
    if (!sanitized) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Обязательное поле',
      })
    }
    if (sanitized > 999999999) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Превышена допустимая стоимость',
      })
    }
  })
  .transform(sanitizePriceNumber)

export const optionSchema = z.object({
  id: z.string(),
  label: z.string(),
})
export const uuidOptionSchema = z
  .union([z.string().uuid(), optionSchema])
  .transform((arg) => {
    if (typeof arg === 'string') {
      return arg
    }
    return arg.id
  })
export const uuidOptionsSchema = z.array(uuidOptionSchema)

export const enumOptionSchema = <T extends EnumLike>(
  values: T,
  errorMessage = 'Выберите значение из списка',
) =>
  z
    .union([
      z.nativeEnum(values, {
        errorMap: () => ({ message: errorMessage }),
      }),
      z.object({
        id: z.nativeEnum(values, {
          errorMap: () => ({ message: errorMessage }),
        }),
        label: z.string(),
      }),
    ])
    .superRefine((value, ctx) => {
      if (value === undefined || value === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: errorMessage,
        })
      }
    })
    .transform((arg) => {
      if (typeof arg === 'object' && 'id' in arg) {
        return arg.id
      }
      return arg
    })

export const yearSchema = z
  .union([
    z
      .string()
      .regex(
        /^\d{4}$/,
        'Год имеет неправильный формат, ожидается, например "2022"',
      ),
    z.number(),
  ])
  .transform((arg) => {
    if (typeof arg === 'string') return parseInt(arg)
    return arg
  })
const yearsSeparatedByCommasRegex = /\b\d{4}\b(?:,\s*\b\d{4}\b)*/g
export const yearsSeparatedByCommasSchema = (min = 0) =>
  z
    .union([
      z
        .string()
        .regex(
          yearsSeparatedByCommasRegex,
          'Года имеют неправильный формат, ожидается, например "1999,2004,2022"',
        ),
      z.array(yearSchema).min(min),
    ])
    .transform((arg) => {
      if (typeof arg === 'string') {
        return arg.match(/\d{4}/g)?.map(Number)
      }
      return arg
    })

export const numberRequiredScheme = z.coerce
  .number({
    invalid_type_error: 'Обязательное поле',
  })
  .min(0.01, 'Обязательное поле')

export const stringRequiredScheme = z
  .string()
  .trim()
  .min(1, 'Обязательное поле')

export const okpoRequiredSchema = z
  .string()
  .trim()
  .regex(/^\d{8}$|^\d{10}$/, 'Только цифры, длина ОКПО - 8 или 10 символов')

export const innRequiredSchema = z
  .string()
  .trim()
  .regex(/^\d{12}$/, 'Только цифры, длина ИНН - 12 символов')

export const kppRequiredSchema = z
  .string()
  .trim()
  .refine(
    (value) => /^\d{9}$/.test(value),
    'Только цифры, длина КПП - 9 символов',
  )

export const ogrnRequiredSchema = z
  .string()
  .trim()
  .refine(
    (value) => /^\d{13}$/.test(value),
    'Только цифры, длина ОГРН - 13 символов',
  )

export const bankRsRequiredSchema = z
  .string()
  .trim()
  .refine(
    (value) => /^\d{20}$/.test(value),
    'Только цифры, длина поля - 20 символов',
  )

export const bankBikRequiredSchema = z
  .string()
  .trim()
  .refine(
    (value) => /^\d{9}$/.test(value),
    'Только цифры, длина БИК - 9 символов',
  )

function validateSnils(snils: string) {
  // www.kholenkov.ru/data-validation/snils/#:~:text=%D0%9E%D0%BF%D0%B8%D1%81%D0%B0%D0%BD%D0%B8%D0%B5,%2D%D1%8F%20%D1%86%D0%B8%D1%84%D1%80%D1%8B%20%E2%80%94%20%D0%BA%D0%BE%D0%BD%D1%82%D1%80%D0%BE%D0%BB%D1%8C%D0%BD%D0%BE%D0%B5%20%D1%87%D0%B8%D1%81%D0%BB%D0%BE

  if (!snils.length) {
    return 'Обязательное поле'
  } else if (snils.length !== 11) {
    return 'СНИЛС - может состоять только из 11 цифр'
  } else {
    let sum = 0
    for (let i = 0; i < 9; i++) {
      sum += Number(snils[i]) * (9 - i)
    }
    let checkDigit = 0
    if (sum < 100) {
      checkDigit = sum
    } else if (sum > 101) {
      checkDigit = Number(sum % 101)
      if (checkDigit === 100) {
        checkDigit = 0
      }
    }
    if (checkDigit !== Number(snils.slice(-2))) {
      return 'СНИЛС - Неправильное контрольное число'
    }
  }
}

export const snilsScheme = z
  .string()
  .superRefine((value, ctx) => {
    const sanitized = value.replace(/-/g, '')

    const errorMessage = validateSnils(sanitized)
    if (errorMessage) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: errorMessage,
      })
    }
  })
  .transform((value) => value.replace(/-/g, ''))

export const pickImageRequiredSchema = z
  .object({
    originalUrlDownload: z.string(),
    previewUrlDownload: z.string(),
    uuid: z.string(),
  })
  .superRefine((value, ctx) => {
    if (!value) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Обязательное поле',
      })
    }
  })
  .transform((arg) => arg.uuid)

export const pickImagesRequiredSchema = z
  .array(pickImageRequiredSchema)
  .min(1, 'Обязательное поле')

const emailSchema = z
  .string({ required_error: 'Обязательное поле' })
  .min(1, 'Обязательное поле')
  .email('Неверный формат email')
export default emailSchema

export const promoCodeDetailsSchema = z.object({
  discount_amount: z.number().optional(),
  duration_days: z.number(),
  discount_percent: z.number().optional(),
  title: z.string().optional(),
  description: z.string().optional(),
})
